import { useEffect, useState } from "react";
import type { Conversation } from "@swift/shared";
import { api } from "../lib/api";
import { loadSettings } from "../lib/settings";
import { useStore } from "../lib/store";
import { scrollToMessage } from "../lib/scrollToMessage";

/**
 * Bestandsbeheer-modal per gesprek. Tabs voor afbeeldingen (galerij),
 * documenten (lijst), audio (incl. spraakberichten) en video. Klik
 * op een afbeelding opent de lightbox; klik op een ander item springt
 * naar het oorspronkelijke bericht in de stream (laadt zo nodig oudere
 * pagina's, gelijk aan PinnedStrip/MentionsDrawer).
 *
 * Paginated via `before=<id>`; default 50 per fetch. Voor afbeeldingen
 * fetchen we de blob met onze auth-header en bouwen 'n object-URL —
 * 't `<img src="/api/v1/attachments/N">`-pad zou geen Bearer-token mee
 * krijgen en 401 geven.
 */

type Tab = "image" | "file" | "audio" | "video";
const TAB_LABELS: Record<Tab, string> = {
  image: "Afbeeldingen",
  file: "Documenten",
  audio: "Audio",
  video: "Video",
};

type AttachmentInfo = {
  id: number;
  filename: string;
  mime: string;
  size: number;
  url: string;
  message_id: number;
  conversation_id: number;
  uploader_id: number;
  created_at: string;
};

export default function FileBrowser({
  conversation,
  onClose,
}: {
  conversation: Conversation;
  onClose(): void;
}) {
  const [tab, setTab] = useState<Tab>("image");
  const [items, setItems] = useState<AttachmentInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Per tab opnieuw laden — geen merging tussen tabs, voorkomt verwarring
  // bij wisselen.
  useEffect(() => {
    let cancelled = false;
    setError(null);
    setItems([]);
    setHasMore(false);
    setLoading(true);
    void (async () => {
      try {
        const r = await api.listConversationAttachments(conversation.id, tab, undefined, 50);
        if (cancelled) return;
        setItems(r.attachments);
        setHasMore(r.attachments.length === 50);
      } catch {
        if (!cancelled) setError("Laden mislukt");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [conversation.id, tab]);

  async function loadMore() {
    if (loading || !hasMore || items.length === 0) return;
    setLoading(true);
    try {
      const before = items[items.length - 1]!.id;
      const r = await api.listConversationAttachments(conversation.id, tab, before, 50);
      setItems((prev) => [...prev, ...r.attachments]);
      setHasMore(r.attachments.length === 50);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-40 flex items-start justify-center bg-black/40 p-4 pt-12"
      onClick={onClose}
    >
      <div
        className="flex max-h-[85vh] w-full max-w-3xl flex-col overflow-hidden rounded-lg border border-slate-700 bg-slate-900 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 border-b border-slate-700 bg-slate-800 px-4 py-2">
          <span className="text-base">📁</span>
          <h2 className="flex-1 text-sm font-medium text-slate-200">
            Bestanden in {conversation.kind === "channel" ? `#${conversation.name ?? "kanaal"}` : "dit gesprek"}
          </h2>
          <button
            onClick={onClose}
            className="rounded p-1 text-slate-400 hover:bg-slate-700 hover:text-slate-100"
            aria-label="Sluiten"
          >
            ✕
          </button>
        </div>
        <div className="flex shrink-0 border-b border-slate-700 bg-slate-800/60 text-xs">
          {(Object.keys(TAB_LABELS) as Tab[]).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`flex-1 border-b-2 px-3 py-2 ${
                tab === t
                  ? "border-accent text-slate-100"
                  : "border-transparent text-slate-400 hover:bg-slate-800"
              }`}
            >
              {TAB_LABELS[t]}
            </button>
          ))}
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-3">
          {error && (
            <div className="py-6 text-center text-sm text-red-400">{error}</div>
          )}
          {!error && items.length === 0 && !loading && (
            <div className="py-6 text-center text-sm text-slate-500">
              Geen {TAB_LABELS[tab].toLowerCase()} in dit gesprek.
            </div>
          )}
          {tab === "image" ? (
            <ImageGrid items={items} onClose={onClose} />
          ) : (
            <FileList items={items} onClose={onClose} />
          )}
          {hasMore && (
            <div className="mt-3 flex justify-center">
              <button
                onClick={() => void loadMore()}
                disabled={loading}
                className="rounded bg-slate-700 px-3 py-1 text-xs text-slate-200 hover:bg-slate-600 disabled:opacity-50"
              >
                {loading ? "Bezig…" : "Meer laden"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ImageGrid({ items, onClose }: { items: AttachmentInfo[]; onClose(): void }) {
  const openLightbox = useStore((s) => s.openLightbox);
  return (
    <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
      {items.map((a) => (
        <ImageTile
          key={a.id}
          att={a}
          onOpen={(url) => {
            openLightbox(url, a.filename);
            onClose();
          }}
        />
      ))}
    </div>
  );
}

function ImageTile({
  att,
  onOpen,
}: {
  att: AttachmentInfo;
  onOpen(url: string): void;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    let cancelled = false;
    let created: string | null = null;
    (async () => {
      try {
        const { server_url, token } = loadSettings();
        const res = await fetch(server_url.replace(/\/$/, "") + att.url, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        if (cancelled) return;
        created = URL.createObjectURL(blob);
        setUrl(created);
      } catch {
        if (!cancelled) setError(true);
      }
    })();
    return () => {
      cancelled = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [att.url]);

  return (
    <button
      onClick={() => url && onOpen(url)}
      className="aspect-square overflow-hidden rounded border border-slate-700 bg-slate-800 hover:border-slate-500"
      title={att.filename}
    >
      {url ? (
        <img src={url} alt={att.filename} className="h-full w-full object-cover" />
      ) : (
        <div className="flex h-full w-full items-center justify-center text-[10px] text-slate-500">
          {error ? "fout" : "…"}
        </div>
      )}
    </button>
  );
}

function FileList({ items, onClose }: { items: AttachmentInfo[]; onClose(): void }) {
  const users = useStore((s) => s.users);

  async function jumpTo(messageId: number, conversationId: number) {
    onClose();
    await scrollToMessage({ conversationId, messageId, switchActive: true });
  }

  return (
    <ul className="divide-y divide-slate-700/60">
      {items.map((a) => {
        const uploader = users[a.uploader_id]?.display_name ?? `Gebruiker #${a.uploader_id}`;
        const time = new Date(a.created_at).toLocaleString("nl-NL", {
          day: "numeric",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });
        const sizeKB = Math.round(a.size / 1024);
        return (
          <li key={a.id}>
            <button
              onClick={() => void jumpTo(a.message_id, a.conversation_id)}
              className="flex w-full items-center gap-3 py-2 text-left hover:bg-slate-800"
            >
              <span className="shrink-0 text-lg">📎</span>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm text-slate-100">{a.filename}</div>
                <div className="truncate text-xs text-slate-500">
                  {uploader} · {time} · {sizeKB} KB · {a.mime}
                </div>
              </div>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
